import { Col, Row, Form, Button, InputGroup } from 'react-bootstrap';
import { useAppContext } from 'providers/AppProvider';
import { useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';
import { RootState } from 'store';

import EmailRow from 'components/modules/email/EmailRow';
import axios from 'axios';
import { Pagination } from 'react-bootstrap';

import { Ticket } from 'data/Ticket';

interface ApiResponse {
  page: number;
  limit: number;
  total_tickets: number;
  tickets: Ticket[];
}

const API_BASE = 'https://api20.easychatdesk.com';

const TicketsPage = () => {
  const token = useSelector((state: RootState) => state.auth.token);

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(8);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderType, setOrderType] = useState<'date'>('date');

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // --- New filter state ---
  const [q, setQ] = useState<string>('');               // search in text (subject/body)
  const [name, setName] = useState<string>('');         // customer name
  const [email, setEmail] = useState<string>('');       // customer email
  const [dateFrom, setDateFrom] = useState<string>(''); // yyyy-mm-dd
  const [dateTo, setDateTo] = useState<string>('');     // yyyy-mm-dd

  // Debounced copies so we don't refetch on every keystroke
  const [qDeb, setQDeb] = useState(q);
  const [nameDeb, setNameDeb] = useState(name);
  const [emailDeb, setEmailDeb] = useState(email);

  useEffect(() => {
    const t = setTimeout(() => setQDeb(q), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const t = setTimeout(() => setNameDeb(name), 350);
    return () => clearTimeout(t);
  }, [name]);

  useEffect(() => {
    const t = setTimeout(() => setEmailDeb(email), 350);
    return () => clearTimeout(t);
  }, [email]);

  const axiosInstance = useMemo(() => {
    const inst = axios.create({
      baseURL: API_BASE,
      timeout: 15000
    });
    return inst;
  }, []);

  const fetchTickets = async (page: number) => {
    if (!token) {
      setErrorMsg('Missing auth token.');
      return;
    }
    setLoading(true);
    setErrorMsg(null);

    try {
      // Build params dynamically (only send if filled)
      const params: Record<string, string | number> = {
        page,
        limit: itemsPerPage,
        order,
        orderType
      };
      if (qDeb.trim()) params.q = qDeb.trim();
      if (nameDeb.trim()) params.name = nameDeb.trim();
      if (emailDeb.trim()) params.email = emailDeb.trim();
      if (dateFrom) params.dateFrom = dateFrom; // expect backend to interpret as inclusive start (YYYY-MM-DD)
      if (dateTo) params.dateTo = dateTo;       // expect backend to interpret as inclusive end   (YYYY-MM-DD)

      const response = await axiosInstance.get<ApiResponse>('/get-offline-tickets', {
        headers: {
          Authorization: `Bearer ${token}`
        },
        params,
        withCredentials: false
      });

      setTickets(response.data.tickets || []);
      setTotalItems(response.data.total_tickets || 0);
    } catch (err: any) {
      if (axios.isAxiosError(err)) {
        if (err.response?.status === 401) {
          setErrorMsg('Your session expired or the token is invalid. Please log in again.');
        } else if (err.response?.status === 404) {
          setTickets([]);
          setTotalItems(0);
        } else {
          setErrorMsg('Failed to fetch tickets. Please try again.');
        }
      } else {
        setErrorMsg('Unexpected error occurred.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Trigger fetch on paging, sort, token, and debounced filters or date range
  useEffect(() => {
    fetchTickets(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, order, orderType, token, qDeb, nameDeb, emailDeb, dateFrom, dateTo]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Apply button: reset to page 1 and let effect do the fetch
  const applyFilters = () => {
    setCurrentPage(1);
    // debounced text inputs are already wired; date changes are immediate
  };

  const clearFilters = () => {
    setQ('');
    setName('');
    setEmail('');
    setDateFrom('');
    setDateTo('');
    setCurrentPage(1);
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const {
    config: { isDark }
  } = useAppContext();

  return (
    <>
      <Row className="gy-3 mb-4 justify-content-between">
        <Col md={12} xs="auto">
          <h2 className="mb-2 text-body-emphasis">Tickets</h2>
          <h5 className="text-body-tertiary fw-semibold">
            See previous tickets and reply to your customers with offline ticket replies.
          </h5>
        </Col>
      </Row>

      {/* Filters */}
      <Form className="mb-3">
        <Row className="g-2">
          <Col md={4}>
            <Form.Label className="mb-1">Search text</Form.Label>
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Subject or message text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </InputGroup>
          </Col>

          <Col md={3}>
            <Form.Label className="mb-1">Customer name</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. John Doe"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Col>

          <Col md={3}>
            <Form.Label className="mb-1">Customer email</Form.Label>
            <Form.Control
              type="email"
              placeholder="e.g. john@site.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Col>

          <Col md={2}>
            <Form.Label className="mb-1">Order</Form.Label>
            <Form.Select
              value={order}
              onChange={(e) => setOrder(e.target.value as 'asc' | 'desc')}
            >
              <option value="desc">Newest first</option>
              <option value="asc">Oldest first</option>
            </Form.Select>
          </Col>
        </Row>

        <Row className="g-2 mt-1">
          <Col md={3}>
            <Form.Label className="mb-1">From date</Form.Label>
            <Form.Control
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </Col>

          <Col md={3}>
            <Form.Label className="mb-1">To date</Form.Label>
            <Form.Control
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </Col>

          <Col md={3}>
            <Form.Label className="mb-1">Order type</Form.Label>
            <Form.Select
              value={orderType}
              onChange={(e) => setOrderType(e.target.value as 'date')}
            >
              <option value="date">Date updated</option>
              {/* Add other order types when backend supports them */}
            </Form.Select>
          </Col>

          <Col md={3} className="d-flex align-items-end gap-2">
            <Button variant="primary" onClick={applyFilters}>Apply</Button>
            <Button variant="outline-secondary" onClick={clearFilters}>Clear</Button>
          </Col>
        </Row>
      </Form>

      {/* Status row */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <small className="text-body-tertiary">
          {totalItems} result{totalItems === 1 ? '' : 's'}
        </small>
        <small className="text-body-tertiary">Page {currentPage} of {Math.max(totalPages, 1)}</small>
      </div>

      {loading && <p className="text-body-tertiary">Loading tickets…</p>}
      {errorMsg && <p className="text-danger">{errorMsg}</p>}

      {!loading && !errorMsg && tickets.length === 0 && (
        <p className="text-body-tertiary">No tickets found.</p>
      )}

      {!loading && !errorMsg && tickets.length > 0 && (
        <>
          {tickets.map((ticket) => (
            <EmailRow key={ticket.id} index={ticket.id} ticket={ticket} />
          ))}

          <Pagination className="justify-content-center mt-4">
            {Array.from({ length: totalPages }, (_, i) => (
              <Pagination.Item
                key={i + 1}
                active={i + 1 === currentPage}
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  );
};

export default TicketsPage;
